<script setup>
import { computed, h } from 'vue';
import { useI18n } from 'vue-i18n';
import SummaryReportLink from './SummaryReportLink.vue';
import SummaryArchiveLink from './SummaryArchiveLink.vue';
import Pagination from 'dashboard/components/table/Pagination.vue';
import { formatReportDuration } from '../constants';
import { useReportTablePagination } from '../composables/useReportTablePagination';
import { useReportTableSort } from '../composables/useReportTableSort';

const props = defineProps({
  teams: {
    type: Array,
    default: () => [],
  },
  teamMetrics: {
    type: Array,
    default: () => [],
  },
  teamComparisons: {
    type: Array,
    default: () => [],
  },
  archiveContext: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();

const teamArchiveContext = computed(() => ({
  ...props.archiveContext,
  reportType: 'team',
}));

const getMetrics = id =>
  props.teamMetrics.find(metric => metric.id === Number(id)) || {};

const renderComparisonLines = (row, accessor, primaryNode) => {
  const comparisonValues = props.teamComparisons
    .map((_, index) => row[`cmp${index}_${accessor}`])
    .filter(value => value && value !== '--');

  if (!comparisonValues.length) return primaryNode;

  return h('div', { class: 'flex flex-col items-center gap-0.5 min-w-0' }, [
    primaryNode,
    ...comparisonValues.map(value =>
      h(
        'span',
        { class: 'text-[9px] leading-tight text-n-slate-11 tabular-nums' },
        value
      )
    ),
  ]);
};

const rawMetricKey = accessor => {
  if (accessor === 'resolutionsCount') return 'resolvedConversationsCount';
  if (accessor === 'assignmentEpisodesCount') return 'uniqueCyclesHandled';
  return accessor;
};

const renderCountCell = (row, accessor, metricKey) => {
  const rawMetrics = getMetrics(row.id);
  const rawKey = rawMetricKey(accessor);

  const link = h(SummaryArchiveLink, {
    value: row[accessor],
    metricKey,
    row,
    archiveContext: teamArchiveContext.value,
    rawValue: rawMetrics[rawKey],
  });

  return renderComparisonLines(row, accessor, link);
};

const renderTimeCell = (row, accessor, metricKey = null, rawValue = null) => {
  const value = row[accessor];
  const node = metricKey
    ? h(SummaryArchiveLink, {
        value: value || '--',
        metricKey,
        row,
        archiveContext: teamArchiveContext.value,
        rawValue,
      })
    : h(
        'span',
        { class: value && value !== '--' ? 'tabular-nums' : 'text-n-slate-11' },
        value || '--'
      );
  return renderComparisonLines(row, accessor, node);
};

const columns = computed(() => [
  {
    key: 'name',
    label: t('SUMMARY_REPORTS.TEAM'),
    cell: row =>
      h(SummaryReportLink, {
        row: { original: { ...row, type: 'team' } },
        compact: true,
      }),
  },
  {
    key: 'assignmentEpisodesCount',
    label: t('SUMMARY_REPORTS.HEADER_SHORT.TEAM_RECEIVED'),
    cell: row =>
      renderCountCell(row, 'assignmentEpisodesCount', 'uniqueCyclesHandled'),
  },
  {
    key: 'resolutionsCount',
    label: t('SUMMARY_REPORTS.HEADER_SHORT.RESOLUTION_COUNT'),
    cell: row => renderCountCell(row, 'resolutionsCount', 'resolutionsCount'),
  },
  {
    key: 'medianFirstResponseTime',
    label: t('SUMMARY_REPORTS.HEADER_SHORT.MEDIAN_FIRST_RESPONSE_TIME'),
    cell: row => {
      const metrics = getMetrics(row.id);
      return renderTimeCell(
        row,
        'medianFirstResponseTime',
        'firstResponseCount',
        metrics.medianFirstResponseTime == null ||
          metrics.medianFirstResponseTime === ''
          ? 0
          : 1
      );
    },
  },
  {
    key: 'medianResolutionTime',
    label: t('SUMMARY_REPORTS.HEADER_SHORT.MEDIAN_RESOLUTION_TIME'),
    cell: row => {
      const metrics = getMetrics(row.id);
      return renderTimeCell(
        row,
        'medianResolutionTime',
        'resolutionsCount',
        metrics.resolvedConversationsCount
      );
    },
  },
  {
    key: 'csat',
    label: t('SUMMARY_REPORTS.HEADER_SHORT.CSAT'),
    cell: row => {
      const metrics = getMetrics(row.id);
      return renderTimeCell(
        row,
        'csat',
        'csatResponses',
        metrics.csatResponses || 0
      );
    },
  },
]);

const formatCount = value => (value ? value.toLocaleString() : '--');

const renderCsat = (average, responses) => {
  if (average === null || average === undefined || average === '') return '--';
  return `${Number(average).toFixed(1)}/${responses || 0}`;
};

const sliceRowMetrics = (slice, id) =>
  (slice?.data || []).find(metric => metric.id === Number(id)) || {};

const tableRows = computed(() =>
  props.teams
    .map(team => {
      const metrics = getMetrics(team.id);
      const hasActivity =
        (metrics.uniqueCyclesHandled || 0) > 0 ||
        (metrics.resolvedConversationsCount || 0) > 0 ||
        (metrics.backlogEnd || 0) > 0;
      if (!hasActivity) return null;

      const row = {
        id: team.id,
        name: team.name,
        type: 'team',
        assignmentEpisodesCount: formatCount(metrics.uniqueCyclesHandled),
        resolutionsCount: formatCount(metrics.resolvedConversationsCount),
        medianFirstResponseTime: formatReportDuration(
          metrics.medianFirstResponseTime,
          t
        ),
        medianResolutionTime: formatReportDuration(
          metrics.medianFullResolutionSeconds || metrics.medianResolutionTime,
          t
        ),
        csat: renderCsat(metrics.csatAverage, metrics.csatResponses),
        sort_assignmentEpisodesCount: metrics.uniqueCyclesHandled || 0,
        sort_resolutionsCount: metrics.resolvedConversationsCount || 0,
        sort_medianFirstResponseTime:
          Number(metrics.medianFirstResponseTime) || 0,
        sort_medianResolutionTime:
          Number(
            metrics.medianFullResolutionSeconds || metrics.medianResolutionTime
          ) || 0,
        sort_csat: Number(metrics.csatAverage) || 0,
      };

      props.teamComparisons.forEach((slice, index) => {
        const comparisonMetrics = sliceRowMetrics(slice, team.id);
        row[`cmp${index}_assignmentEpisodesCount`] = formatCount(
          comparisonMetrics.uniqueCyclesHandled
        );
        row[`cmp${index}_resolutionsCount`] = formatCount(
          comparisonMetrics.resolvedConversationsCount
        );
        row[`cmp${index}_medianFirstResponseTime`] = formatReportDuration(
          comparisonMetrics.medianFirstResponseTime,
          t
        );
        row[`cmp${index}_medianResolutionTime`] = formatReportDuration(
          comparisonMetrics.medianFullResolutionSeconds ||
            comparisonMetrics.medianResolutionTime,
          t
        );
        row[`cmp${index}_csat`] = renderCsat(
          comparisonMetrics.csatAverage,
          comparisonMetrics.csatResponses
        );
      });

      return row;
    })
    .filter(Boolean)
);

const { sortKey, sortDir, searchQuery, toggleSort, sortedRows } =
  useReportTableSort(tableRows, 'assignmentEpisodesCount', {
    numericKeys: [
      'assignmentEpisodesCount',
      'resolutionsCount',
      'medianFirstResponseTime',
      'medianResolutionTime',
      'csat',
    ],
  });

const { table, pagedRows, getPageSize, handlePageSizeChange } =
  useReportTablePagination(sortedRows, 'overview_teams_table_page_size');
</script>

<template>
  <section
    class="relative mt-5 px-2 py-2 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
  >
    <h3 class="px-2 py-2 text-base font-medium text-n-slate-12">
      {{ $t('OVERVIEW_REPORTS.TEAMS_TABLE.TITLE') }}
    </h3>
    <div class="mb-2 flex justify-end px-2">
      <input
        v-model="searchQuery"
        type="search"
        class="w-full max-w-xs rounded-lg border border-n-weak bg-n-solid-1 px-3 py-1.5 text-sm text-n-slate-12 outline-none focus:border-n-brand"
        :placeholder="$t('SUMMARY_REPORTS.TABLE_SEARCH')"
      />
    </div>
    <div class="overflow-x-auto">
      <table class="w-full table-fixed text-xs">
        <thead>
          <tr class="border-b border-n-weak text-[10px] text-n-slate-11">
            <th
              v-for="column in columns"
              :key="column.key"
              class="px-2 py-2 text-left font-medium cursor-pointer select-none"
              @click="toggleSort(column.key)"
            >
              {{ column.label }}
              <span v-if="sortKey === column.key">{{
                sortDir === 'desc' ? '↓' : '↑'
              }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in pagedRows"
            :key="row.id"
            class="border-b border-n-weak/60 last:border-b-0"
          >
            <td
              v-for="column in columns"
              :key="column.key"
              class="px-2 py-2 text-center text-n-slate-12"
              :class="{ 'text-left': column.key === 'name' }"
            >
              <component :is="column.cell(row)" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <Pagination
      v-if="tableRows.length"
      class="mt-2 px-2"
      :table="table"
      show-page-size-selector
      :default-page-size="getPageSize()"
      @page-size-change="handlePageSizeChange"
    />
    <p
      v-if="!tableRows.length"
      class="px-2 py-6 text-center text-sm text-n-slate-11"
    >
      {{ $t('SUMMARY_REPORTS.CHARTS.NO_DATA') }}
    </p>
  </section>
</template>
